<script setup lang="ts">
import { ICareerResponse } from '@shared/types'
import { computed } from 'vue'
import SubjectTable from './SubjectTable.vue'
import { formatDate } from '@/utils/dateFormatter'
import { EAuthorizationType, ETipoCarrera, NOMBRE_AUTORIZACION, NOMBRE_TIPO_CARRERA } from '@shared/types/enums'

const props = defineProps<{
	carreras: ICareerResponse[]
}>()

const carreras = computed(() => {
	if (!props.carreras || !Array.isArray(props.carreras)) {
		return []
	}
	return props.carreras.filter((carrera: any) => carrera !== undefined && carrera !== null)
})
</script>

<template>
	<div class="certificates-scroll">
		<VaAccordion class="career-accordion">
			<VaCollapse v-for="carrera in carreras" :key="carrera.idCarrera" class="career-collapse">
				<template #header>
					<div class="career-header">
						<div class="career-info">
							<VaAvatar icon="cases" color="#062342" size="small" class="career-icon" />
							<div class="career-details">
								<span class="career-name">{{ carrera.nombreCarrera }}</span>
								<span class="career-count">{{ Object.keys(carrera.Revoes || {}).length }} RVOE{{ Object.keys(carrera.Revoes || {}).length !== 1 ? 's' : '' }}</span>
							</div>
						</div>
						<div class="career-arrow">
							<VaIcon name="expand_more" size="small" />
						</div>
					</div>
				</template>
				<template #content>
					<div class="career-content">
						<!-- Tabs para RVOEs -->
						<VaCard v-for="rvoe in carrera.Revoes" :key="rvoe.idAutorizacionReconocimiento" class="rvoe-card">
							<VaCardContent class="rvoe-card-content">
								<div class="rvoe-header">
									<div class="rvoe-badge">
										<VaIcon name="verified" color="success" size="small" />
										<span class="rvoe-number">{{ NOMBRE_AUTORIZACION[rvoe.idAutorizacionReconocimiento as EAuthorizationType] }}: {{ rvoe.numero }}</span>
										<div style="display: flex; align-items: center">
											<div style="display: flex; align-items: center; margin: 10px">
												<i class="fas fa-book-open text-gray-400" style="margin-right: 10px; font-size: 11px; color: #062342"></i>
												<span style="font-weight: bold; font-size: 11px">Calificacion maxima: {{ rvoe.calificacionMaxima }}</span>
											</div>
											<div style="display: flex; align-items: center; margin: 10px">
												<i class="fas fa-book-open text-gray-400" style="margin-right: 10px; font-size: 11px; color: #062342"></i>
												<span style="font-weight: bold; font-size: 11px">Calificacion minima: {{ rvoe.calificacionMinima }}</span>
											</div>
											<div style="display: flex; align-items: center; margin: 10px">
												<i class="fas fa-book-open text-gray-400" style="margin-right: 10px; font-size: 11px; color: #062342"></i>
												<span style="font-weight: bold; font-size: 11px">Calificacion minima aprobatoria: {{ rvoe.calificacionMinimaAprobatoria }}</span>
											</div>
											<div style="display: flex; align-items: center; margin: 10px">
												<i class="fas fa-clock text-gray-400" style="margin-right: 10px; font-size: 11px; color: #062342"></i>
												<span style="font-weight: bold; font-size: 11px">Fecha de expedicion: {{ formatDate(rvoe.fechaExpedicion) }}</span>
											</div>
										</div>
									</div>
									<div class="rvoe-status">
										<VaBadge color="success" text="Activo" />
									</div>
								</div>

								<!-- Grid de planes -->
								<div class="plans-grid">
									<VaCard v-for="(plan, planKey) in rvoe.Planes" :key="planKey" class="plan-card" :class="{ 'plan-card-active': planKey === Object.keys(rvoe.Planes)[0] }">
										<VaCardContent class="plan-content">
											<VaAccordion v-if="Object.keys(rvoe.Planes).length > 1" style="width: 100%">
												<VaCollapse :header="plan.clavePlan.toString()" style="margin-bottom: 10px">
													<template #header>
														<div class="plan-header">
															<div class="plan-info">
																<VaIcon name="school" color="primary" size="small" />
																<span class="plan-title">Plan: {{ plan.clavePlan }}</span>
																<div style="display: flex">
																	<div style="display: flex; align-items: center; margin: 10px">
																		<i class="fas fa-coins text-gray-400" style="margin-right: 10px; font-size: 11px; color: #062342"></i>
																		<span style="font-weight: bold; font-size: 11px">Total de creditos: {{ plan.totalCreditos }}</span>
																	</div>
																	<div style="display: flex; align-items: center; margin: 10px">
																		<i class="fas fa-coins text-gray-400" style="margin-right: 10px; font-size: 11px; color: #062342"></i>
																		<span style="font-weight: bold; font-size: 11px">Total de asignaturas para acreditar: {{ plan.totalAsignaturas }}</span>
																	</div>
																	<div style="display: flex; align-items: center; margin: 10px">
																		<i class="fas fa-book-open text-gray-400" style="margin-right: 10px; font-size: 11px; color: #062342"></i>
																		<span style="font-weight: bold; font-size: 11px">Asignaturas: {{ Object.keys(plan.Asignaturas || {}).length }}</span>
																	</div>
																	<div style="display: flex; align-items: center; margin: 10px">
																		<i class="fas fa-user-graduate text-gray-400" style="margin-right: 10px; font-size: 11px; color: #062342"></i>
																		<span style="font-weight: bold; font-size: 11px">Tipo de carrera: {{ NOMBRE_TIPO_CARRERA[plan.tipoCarrera as ETipoCarrera] || 'Sin Tipo de Carrera' }}</span>
																	</div>
																</div>
															</div>
															<VaIcon name="expand_more" size="small" color="primary" />
														</div>
													</template>
													<template #content>
														<div class="subjects-section">
															<SubjectTable :asignaturas="plan.Asignaturas" />
														</div>
													</template>
												</VaCollapse>
											</VaAccordion>
											<div v-else>
												<div class="plan-header">
													<div class="plan-info">
														<VaIcon name="school" color="primary" size="small" />
														<span class="plan-title">Plan: {{ plan.clavePlan }}</span>
													</div>
													<div style="display: flex; align-items: center">
														<div style="display: flex; align-items: center; margin: 10px">
															<i class="fas fa-coins text-gray-400" style="margin-right: 10px; font-size: 11px; color: #062342"></i>
															<span style="font-weight: bold; font-size: 11px">Total de asignaturas para acreditar: {{ plan.totalAsignaturas }}</span>
														</div>
														<div style="display: flex; align-items: center; margin: 10px">
															<i class="fas fa-book-open text-gray-400" style="margin-right: 10px; font-size: 11px; color: #062342"></i>
															<span style="font-weight: bold; font-size: 11px">Asignaturas: {{ Object.keys(plan.Asignaturas || {}).length }}</span>
														</div>
														<div style="display: flex; align-items: center; margin: 10px">
															<i class="fas fa-coins text-gray-400" style="margin-right: 10px; font-size: 11px; color: #062342"></i>
															<span style="font-weight: bold; font-size: 11px">Total de creditos: {{ plan.totalCreditos }}</span>
														</div>
														<div style="display: flex; align-items: center; margin: 10px">
															<i class="fas fa-user-graduate text-gray-400" style="margin-right: 10px; font-size: 11px; color: #062342"></i>
															<span style="font-weight: bold; font-size: 11px">Tipo de carrera: {{ NOMBRE_TIPO_CARRERA[plan.tipoCarrera as ETipoCarrera] || 'Sin Tipo de Carrera' }}</span>
														</div>
													</div>
													<VaIcon name="expand_more" size="small" color="primary" />
												</div>
												<div class="subjects-section">
													<SubjectTable :asignaturas="plan.Asignaturas" />
												</div>
											</div>
										</VaCardContent>
									</VaCard>
								</div>
							</VaCardContent>
						</VaCard>
					</div>
				</template>
			</VaCollapse>
		</VaAccordion>
	</div>
</template>

<style scoped>
.certificates-scroll {
	max-height: 70vh;
	overflow-y: auto;
	padding-right: 8px;
	padding-bottom: 24px;
}

/* Accordion Styles */
.career-accordion {
	--va-accordion-border-radius: 12px;
	--va-accordion-border: none;
	--va-accordion-box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.career-collapse {
	margin-bottom: 16px;
	border-radius: 12px;
	overflow: hidden;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.career-collapse:hover {
	transform: translateY(-2px);
	box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* Header Styles */
.career-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 20px 24px;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	color: white;
	cursor: pointer;
	transition: all 0.3s ease;
}

.career-header:hover {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
}

.career-info {
	display: flex;
	align-items: center;
	gap: 16px;
	flex: 1;
}

.career-icon {
	background: rgba(255, 255, 255, 0.2) !important;
	border: 2px solid rgba(255, 255, 255, 0.3);
}

.career-details {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.career-name {
	font-weight: 600;
	font-size: 16px;
	letter-spacing: 0.5px;
	text-transform: capitalize;
}

.career-count {
	font-size: 12px;
	opacity: 0.9;
	font-weight: 400;
}

.career-arrow {
	transition: transform 0.3s ease;
}

.career-collapse[aria-expanded='true'] .career-arrow {
	transform: rotate(180deg);
}

/* Content Styles */
.career-content {
	padding: 24px;
	background: #fafbfc;
}

/* RVOE Card Styles */
.rvoe-card {
	margin-bottom: 20px;
	border-radius: 12px;
	border: 1px solid #e1e5e9;
	overflow: hidden;
	transition: all 0.3s ease;
}

.rvoe-card:hover {
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
	transform: translateY(-1px);
}

.rvoe-card-content {
	padding: 20px;
}

.rvoe-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 20px;
	padding: 16px;
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-radius: 8px;
	border-left: 4px solid #28a745;
}

.rvoe-badge {
	display: flex;
	align-items: center;
	gap: 8px;
}

.rvoe-number {
	font-weight: 600;
	color: #495057;
	font-size: 14px;
}

.rvoe-status {
	display: flex;
	align-items: center;
}

/* Plans Grid */
.plans-grid {
	grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
	gap: 20px;
	margin-top: 16px;
}

.plan-card {
	border: 1px solid #e1e5e9;
	border-radius: 10px;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	cursor: pointer;
	background: white;
}

.plan-card:hover {
	transform: translateY(-3px);
	box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
	border-color: #007bff;
}

.plan-card-active {
	border-color: #007bff;
	box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
	background: linear-gradient(135deg, #f8fbff 0%, #f0f8ff 100%);
}

.plan-content {
	padding: 20px;
}

.plan-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 16px;
	padding-bottom: 12px;
	border-bottom: 1px solid #e9ecef;
}

.plan-info {
	display: flex;
	align-items: center;
	gap: 8px;
}

.plan-title {
	font-weight: 600;
	color: #495057;
	font-size: 14px;
}

/* Subjects Section */
.subjects-section {
	margin-top: 16px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
	.career-header {
		padding: 16px 20px;
	}

	.career-content {
		padding: 16px;
	}

	.plans-grid {
		grid-template-columns: 1fr;
		gap: 16px;
	}

	.rvoe-header {
		flex-direction: column;
		align-items: flex-start;
		gap: 12px;
	}

	.certificates-scroll {
		max-height: 60vh;
	}
}

@media (max-width: 480px) {
	.career-info {
		gap: 12px;
	}

	.career-name {
		font-size: 14px;
	}

	.plan-content {
		padding: 16px;
	}
}
</style>
